// The role select and the role list (spec/dashboard.md section 12.4): the options carry their description as a title, the
// select its chosen role's (for the table rows, where a line under the control would not fit; the dialogs put it under the
// control through the Field's help), and "What can each role do?" lists every role of a kind with its description.
import { forwardRef, type SelectHTMLAttributes } from 'react';
import { Select } from '../ui/inputs';
import { roleDescription, rolesOf, type RoleKind } from './roles';

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & { size?: 's' | 'm' };

export interface RoleSelectProps extends SelectProps {
  kind: RoleKind;
  /** the role keys offered (defaults to every role of the kind) */
  roles?: string[];
  /** an empty first option ("no game role") */
  emptyLabel?: string;
}

export const RoleSelect = forwardRef<HTMLSelectElement, RoleSelectProps>(function RoleSelect({ kind, roles, emptyLabel, value, title, children, ...rest }, ref) {
  const all = rolesOf(kind);
  const keys = roles ?? all.map((r) => r.key);
  const current = typeof value === 'string' ? value : '';
  return (
    <Select ref={ref} value={value} title={title ?? roleDescription(kind, current)} {...rest}>
      {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
      {current && !keys.includes(current) && <option value={current}>{current}</option>}
      {keys.map((k) => {
        const info = all.find((r) => r.key === k);
        return (
          <option key={k} value={k} title={info?.description}>
            {info?.label ?? k}
          </option>
        );
      })}
      {children}
    </Select>
  );
});

/** "What can each role do?": a disclosure listing every role of the kind with its description. */
export function RoleHelp({ kind, summary = 'What can each role do?' }: { kind: RoleKind; summary?: string }) {
  return (
    <details className="role-help" data-testid={`role-help-${kind}`}>
      <summary>{summary}</summary>
      <dl className="role-help-list">
        {rolesOf(kind).map((r) => (
          <div key={r.key} className="role-help-row">
            <dt>{r.label}</dt>
            <dd>{r.description.replace(/^[^:]+:\s*/, '')}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
